import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Button from './button';
import Dialog from './dialog';

type TProps = Omit<DialogProps, 'title' | 'onClose'> & {
  additionalActions?: ReactNode;
  cancelLabel?: ReactNode;
  confirmLabel?: ReactNode;
  dangerous?: boolean;
  description?: ReactNode;
  loading?: boolean;
  onClose: (confirmed: boolean) => void;
  reversed?: boolean;
  title: ReactNode;
};

function ConfirmDialog({
  loading,
  dangerous,
  title,
  description,
  cancelLabel,
  confirmLabel,
  additionalActions,
  reversed,
  onClose,
  ...props
}: TProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Dialog
      fullWidth
      disableEscapeKeyDown={loading}
      maxWidth="xs"
      onClose={(_, reason) => (!loading || reason !== 'backdropClick') && onClose(false)}
      {...props}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography color={dangerous ? 'error' : 'textSecondary'} variant="caption">
          {description || t('geovelo.confirm_dialog.description')}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          flexDirection: reversed ? 'row-reverse' : 'row',
          gap: 1,
          justifyContent: reversed ? 'flex-start' : 'flex-end',
        }}
      >
        <Button
          color={reversed ? 'primary' : 'inherit'}
          disabled={loading}
          onClick={() => onClose(false)}
          size="small"
          variant={reversed ? 'contained' : 'outlined'}
        >
          {cancelLabel || t('commons.actions.cancel')}
        </Button>
        <Button
          className={dangerous ? 'dangerous' : ''}
          color={dangerous ? 'error' : reversed ? 'inherit' : 'primary'}
          disabled={loading}
          onClick={() => onClose(true)}
          size="small"
          startIcon={loading && <CircularProgress color="inherit" size={16} />}
          variant={reversed ? 'outlined' : 'contained'}
        >
          {confirmLabel || t('commons.actions.confirm')}
        </Button>
        {additionalActions}
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
